import { useEffect, useState } from 'react';

import { Alert, Button } from 'react-bootstrap';
import { useIntl } from 'react-intl';

import { useAxios } from 'configs/requests';
import { useCanMigratePatients } from 'hooks';
import { getApiErrorMessage } from 'utils/api-error';
import { isMigrationOutstanding, migrationRoutes } from 'utils/PatientMigration';

import CancelMigrationModal from './CancelMigrationModal';
import CompleteMigrationModal from './CompleteMigrationModal';

interface IMigrationBannerProps {
  uid: string;
  status: IMigrationStatus | null | undefined;
  onChanged: () => void;
}

const MigrationBanner = ({ uid, status, onChanged }: IMigrationBannerProps) => {
  const { formatMessage } = useIntl();
  const canMigrate = useCanMigratePatients();
  const [showCancel, setShowCancel] = useState(false);
  const [showComplete, setShowComplete] = useState(false);
  const [cancelRefusal, setCancelRefusal] = useState<string | null>(null);

  const [{ loading: cancelling }, cancel] = useAxios(
    { method: 'POST', url: migrationRoutes.cancelMigration(uid) },
    { manual: true }
  );

  // The marker comes from php on the patient payload; only the counts and canCancel come from
  // ts, so this is fetched separately and only while a move is actually outstanding.
  const outstanding = isMigrationOutstanding(status);
  const [{ data: progress }, fetchProgress] = useAxios<IMigrationProgress>(
    { method: 'GET', url: migrationRoutes.status(uid) },
    { manual: true }
  );

  // Gated on canMigrate, not just on the move being outstanding: the status route is
  // @Roles(SYSOP, SUPPORT), so for the clinician who merely has the patient open it answers 403
  // and rejects. The banner's text needs no counts; only the operator's actions do.
  useEffect(() => {
    if (outstanding && canMigrate) fetchProgress();
  }, [outstanding, canMigrate, uid, fetchProgress]);

  if (!outstanding || !status) return null;

  // The modal stays open until this resolves. Closing first swallowed the refusal ts writes for
  // the case that matters: a cancel attempted after a device has already moved.
  const runCancel = async () => {
    setCancelRefusal(null);
    try {
      await cancel();
      setShowCancel(false);
      onChanged();
    } catch (caught) {
      setCancelRefusal(
        getApiErrorMessage(caught, formatMessage({ id: 'migrationCancelFailedKey' }))
      );
    }
  };

  // The banner states the fact for everyone, because a clinician who cannot act still needs to
  // know why the patient behaves oddly. Only the actions are gated.
  return (
    <Alert variant="warning" className="mb-3" data-test="migration-banner">
      <div className="d-flex justify-content-between align-items-center gap-3 flex-wrap">
        <div>
          <div className="fw-bold">
            {formatMessage(
              { id: 'migrationBannerTitleKey' },
              { destination: status.destinationEnvironment }
            )}
          </div>
          {progress && (
            <div>
              {formatMessage(
                { id: 'migrationBannerDevicesKey' },
                { moved: progress.devicesMigrated, total: progress.devicesTotal }
              )}
            </div>
          )}
        </div>
        {canMigrate && (
          <div className="d-flex gap-2">
            <Button
              variant="primary"
              size="sm"
              disabled={cancelling}
              onClick={() => setShowComplete(true)}
              data-test="migration-complete"
            >
              {formatMessage({ id: 'migrationCompleteKey' })}
            </Button>
            {/* Disabled once the source has released a device, which is the point past which
                abandoning the move strands a SIM on the destination. It is not a sufficient
                guard: that count tracks the source releasing, while the SIM actually moves when
                the destination registers it, and the two environments never talk. So for most of
                this banner's life the count reads 0 while the SIMs may already have moved, and
                the cancel confirmation is what covers that window. */}
            <Button
              variant="outline-secondary"
              size="sm"
              disabled={cancelling || progress?.canCancel === false}
              onClick={() => setShowCancel(true)}
              data-test="migration-cancel"
            >
              {formatMessage({ id: 'migrationCancelKey' })}
            </Button>
          </div>
        )}
      </div>
      {showComplete && (
        <CompleteMigrationModal
          uid={uid}
          onCancel={() => setShowComplete(false)}
          onCompleted={() => {
            setShowComplete(false);
            onChanged();
          }}
        />
      )}
      {showCancel && status.destinationEnvironment && (
        <CancelMigrationModal
          destinationEnvironment={status.destinationEnvironment}
          loading={cancelling}
          refusal={cancelRefusal}
          onCancel={() => {
            setCancelRefusal(null);
            setShowCancel(false);
          }}
          onConfirm={runCancel}
        />
      )}
    </Alert>
  );
};

export default MigrationBanner;
